"use client";

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function MobileMenuConsumer({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!isOpen || !mounted) return null;
  return createPortal(<div className="fixed inset-0 z-[100] flex flex-col bg-background/98 backdrop-blur-xl transition-all duration-300 animate-in fade-in zoom-in-95">
      <div className="flex h-20 items-center justify-between px-4 border-b border-border/50">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/elbo_room_logo.png" alt="Elbo Room" className="h-7 w-auto drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]" />
        <Button variant="ghost" size="icon" onClick={onClose} className="text-foreground hover:text-primary hover:bg-accent/20">
          <X className="size-6" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto py-12 px-8">
        {loading ? <div className="space-y-6">
            {[1, 2, 3, 4].map((item, i) => <div key={i} data-index={i} className="h-12 w-full bg-muted animate-pulse rounded-md" />)}
          </div> : <nav className="flex flex-col gap-8">
            {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-3xl font-serif tracking-wider text-foreground hover:text-primary transition-colors" onClick={onClose} data-index={i}>
                {item.label}
              </Link>)}
          </nav>}
      </div>
    </div>, document.body);
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="main-navigation" className={`dark text-foreground sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-border/50 shadow-lg shadow-black/40' : 'bg-background'}`}>
      <MenuProvider menu_id="80">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          
          {}
          <div className="md:hidden flex-1 flex justify-start">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileOpen(true)} className="text-foreground hover:text-primary hover:bg-accent/20">
              <Menu className="size-6" />
            </Button>
          </div>

          {}
          <nav className="hidden md:flex flex-1 items-center justify-end gap-8 pr-8">
            <Link to="#" className="group relative flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-foreground transition-colors hover:text-primary">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive"></span>
              </span>
              Live Now
              <span className="absolute -bottom-2 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link to="/CityGuidePage" className="group relative text-sm font-bold uppercase tracking-wider text-foreground transition-colors hover:text-primary">
              City Guide
              <span className="absolute -bottom-2 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
          </nav>

          {}
          <div className="shrink-0 flex justify-center">
            <Link to="/">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/elbo_room_logo.png" alt="Elbo Room" className="h-7 w-auto drop-shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-transform duration-300 hover:scale-105" />
            </Link>
          </div>

          {}
          <nav className="hidden md:flex flex-1 items-center justify-start gap-8 pl-8">
            <Link to="/CorporatePage" className="group relative text-sm font-bold uppercase tracking-wider text-foreground transition-colors hover:text-primary">
              Corporate
              <span className="absolute -bottom-2 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link to="#" className="group relative text-sm font-bold uppercase tracking-wider text-foreground transition-colors hover:text-primary">
              Real Estate
              <span className="absolute -bottom-2 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
          </nav>

          {}
          <div className="md:hidden flex-1"></div>

        </div>

        <MobileMenuConsumer isOpen={isMobileOpen} onClose={() => setIsMobileOpen(false)} />
      </MenuProvider>
    </section>;
}